import { Injectable } from '@angular/core';

import { map, Observable, of } from 'rxjs';

import { Customer, GetCustomerResponse, GetCustomersData, GetCustomersResponse, SearchCustomersRequest, Visit } from '@shared/customer/models';
import { MergedCustomFields, Party } from '@shared/party';
import { extractPartyFieldsFromCustomer } from '@shared/customer/functions';
import { FieldConfig } from '@shared/restaurant/models';
import { ApiClientService } from '@tablesready/api-client';
import { TelService } from '@tablesready/common-ui';

@Injectable({
  providedIn: 'root'
})
export class CustomersService {
  private readonly basePath = '/v.1/customers';

  constructor(
    private readonly http: ApiClientService,
    private readonly telService: TelService,
  ) { }

  saveCustomer(customer) {
    delete customer.visits;

    return this.http.post<any>(this.basePath, customer).pipe(
      map((response) => {
        if (response.success) {
          return response.data;
        } else {
          throw new Error(`${response.error}`);
        }
      })
    )
  }

  getCustomers(request: SearchCustomersRequest): Observable<GetCustomersData> {
    return this.http.post<GetCustomersResponse>(`${this.basePath}/search`, request).pipe(
      map((response) => {
        if (response.success) {
          return response.data;
        }

        throw new Error(String(response.error));
      }));
  }

  getCustomerById(id: string): Observable<Customer & { visits: Visit[] }> {
    return this.http.get<GetCustomerResponse>(`${this.basePath}/${id}`).pipe(
      map((response) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(String(response.error));
      }));
  }

  deleteCustomersBulk(customerIds: string[]) {
    return this.http.post<any>(`${this.basePath}/delete`, customerIds).pipe(
      map((response) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(String(response.error));
      }));
  }

  customerPhoneLookup(phoneNumber: string) {
    return this.http.get(`/v.1/who/party/${phoneNumber}`).pipe(
      map((response: any) => {
        if (response.success) {
          return response.data;
        }
      }));
  }

  getByPhone(phone: string): Observable<Customer | undefined> {
    const trimmed = typeof phone === 'string' ? phone.trim() : '';
    if (!trimmed) {
      return of(undefined);
    }

    return this.http.get(`${this.basePath}/phone/${encodeURIComponent(trimmed)}`).pipe(
      map((response: any) => {
        if (response.success) {
          return response.data;
        }
      }));
  }

  partyPhoneLookup(
    phone: string,
    country: string,
    partyFields: FieldConfig[],
    shortestPhoneDigits = 4
  ): Observable<MergedCustomFields & {customer: Customer;} | null> {
    if (!phone || phone === '' || typeof phone === 'undefined' || phone.length < shortestPhoneDigits) {
      return of(null);
    }

    const parsedPhone = this.telService.format(phone, country, 'intl');
    if (typeof parsedPhone !== 'undefined' && parsedPhone.length >= shortestPhoneDigits) {
      return this.getByPhone(parsedPhone)
        .pipe(
          map(data => {
            if (data) {
              return {
                ...extractPartyFieldsFromCustomer(partyFields, data),
                customer: data,
              };
            }

            return null;
          }),
        );
    }
  }

  getPartyById(partyId: string): Observable<Party> {
    return this.http.get<Party>(`/v.1/parties/${partyId}`);
  }
}
